import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom/cjs/react-router-dom";
import moment from "moment";
import { useSelector, useDispatch } from "react-redux";
import {
  appActivityList,
  setAppActivityData,
} from "../../AxiosRtk/AppActivity";
import { useHistory } from "react-router-dom";
import useKidListFunctionality from "../../../customHooks/kidListHook";
import ActivityScreen from "./ActivityScreen/ActivityScreen";
import AlertScreen from "./AlertScreen/AlertScreen";
import _debounce from "lodash/debounce";
import CommonStatus from "../Common/CommonStatus";
import useDeviceStatus from "../../../customHooks/deviceStatusProvider";
import useLangTranslation from "../../../customHooks/languageProvider";
import Select from "react-select";
import useFirebase from "../../../../useFirebase";
import LoadingIndicator from "../../../../setup/Loader/LoaderIndicator";
import { useDashboardDataProvider } from "../../../customHooks/dashboardDataProvider";

const AppActivity = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [loader, setLoader] = useState(false);
  const { getLangTranslation } = useLangTranslation();
  const { handleDeviceSelection } = useDashboardDataProvider();
  const { getCollectionData } = useFirebase();
  const { getImage } = useKidListFunctionality();
  const [filterData, setFilterData] = useState(null);
  const [startDate, setStartDate] = useState(
    moment().startOf("day").format("YYYY-MM-DD HH:mm:ss")
  );
  const [endDate, setEndDate] = useState(
    moment().format("YYYY-MM-DD HH:mm:ss")
  );
  const { kidList, selectedKidId, kidDevicesList, selectedDeviceData } =
    useSelector(({ dashboard }) => dashboard);
  const selectedKidData = kidList?.find((item) => item?.ID === selectedKidId);
  const [deviceStatus, setDeviceStatus] = useState("");

  const { getDeviceStatusData } = useDeviceStatus(
    deviceStatus,
    setDeviceStatus
  );

  const handleGoBack = useCallback(() => {
    history.goBack();
  });
  const handleSelectChange = useCallback((event) => {
    const selectedValue = event.target.value;
    let newStartDate;

    switch (selectedValue) {
      case "1":
        // Today
        newStartDate = moment().startOf("day");

        break;
      case "2":
        // First week (starting from Monday of every current week)
        newStartDate = moment().startOf("isoWeek");
        break;
      case "3":
        // Second week (starting from the Monday of the previous Week)
        newStartDate = moment().startOf("isoWeek").subtract(1, "week");
        break;
      case "4":
        // One Month (starting from the 1st day of the current month)
        newStartDate = moment().startOf("month");
        break;
      case "5":
        // All (For Current year)
        newStartDate = moment().startOf("year");
        break;
      default:
        newStartDate = moment();
        break;
    }
    setStartDate(
      moment(newStartDate).locale("en").format("YYYY-MM-DD HH:mm:ss")
    );
  }, []);

  const deviceOptions = useMemo(() => {
    return kidDevicesList?.map((device) => {
      return { value: device?.ID, label: device?.Name };
    });
  }, [kidDevicesList]);

  const debouncedFetchData = useCallback(
    _debounce((payload) => {
      setLoader(true);
      appActivityList(payload)
        .then((res) => {
          dispatch(setAppActivityData(res?.data?.result));
        })
        .catch((err) => {
          console.error(
            "App activity catch error: ",
            err?.response?.fdata?.message
          );
        })
        .finally(() => {
          setLoader(false);
        });
    }, 300),
    []
  );

  useEffect(() => {
    if (selectedDeviceData) {
      const payload = {
        deviceId: selectedDeviceData?.ID,
        startDate,
        endDate,
        pageNo: 1,
        itemsPerPage: 200000,
        appId: filterData?.appIds?.join(","),
      };
      debouncedFetchData(payload);
    }
  }, [selectedDeviceData, startDate, debouncedFetchData, endDate, filterData]);

  useEffect(() => {
    if (selectedDeviceData) {
      getDeviceStatusData(selectedDeviceData?.ID);
      getCollectionData(selectedDeviceData?.ID);
    } else {
      dispatch(setAppActivityData(null));
    }
  }, [selectedDeviceData]);
  
  const AdminSetRuleLanguageData = useSelector(({ setRules }) => setRules);
  const { appLanguageId } = useSelector(({ misc }) => misc);
  const lang =
    AdminSetRuleLanguageData?.AdminSetRuleLanguageData?.languages?.find(
      (item) => item?.id === appLanguageId
    );

  return (
    <>
      {loader && (
        <>
          <LoadingIndicator height={20} width={100} />
          <div className="drawer-overlay" style={{ zIndex: "10" }}></div>
        </>
      )}
      <div className="card mb-6">
        <div className="card-body">
          <div className="inline-list-wrap separator space-24">
            <div className="list-item">
              <div className="list-content">
                <div className="d-inline-flex align-items-center lh-1">
                  <Link
                    to="/"
                    className="text-blue-900 fs-5"
                    onClick={handleGoBack}
                  >
                    <i
                      className={`${
                        lang?.isRTL === 1
                          ? "icon-arrow-right"
                          : "icon-arrow-left"
                      }`}
                    />
                  </Link>
                  <h3>
                    {getLangTranslation(
                      "CHILD_DETAIL_APP_ACTIVITY",
                      "Apps activity"
                    )}
                  </h3>
                </div>
              </div>
            </div>
            <div className="list-item">
              <span className="icon-wrap icon-rounded me-2 p-0 overflow-hidden ratio ratio-1x1 border-0">
                <img
                  className=""
                  src={getImage(
                    selectedKidData?.image,
                    selectedKidData?.gender
                  )}
                  alt="image"
                />
              </span>
              <div>
                <h5 className="text-blue-900 mb-0">
                  {selectedKidData?.childName}
                </h5>
                <CommonStatus status={deviceStatus} />
              </div>
            </div>
            <div className="list-item content-300 w-100">
              <div className="select-with-icon">
                <span className="icon-wrap icon-blue pt-cursor border-0">
                  <i className="icon-mobile fs-5" />
                </span>
                <Select
                  className="custom-select"
                  classNamePrefix="custom-select"
                  placeholder="Select"
                  onChange={handleDeviceSelection}
                  options={deviceOptions}
                  value={
                    deviceOptions?.find(
                      (item) => item?.value === selectedDeviceData?.ID
                    ) || ""
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <nav></nav>
      <div className="tab-content" id="nav-tabContent">
        <div
          className="tab-pane fade show active"
          id="Activity"
          role="tabpanel"
          aria-labelledby="activityTab"
        >
          <ActivityScreen
            handleSelectChange={handleSelectChange}
            selectedDeviceId={selectedDeviceData?.ID}
            startDate={startDate}
            endDate={endDate}
            setEndDate={setEndDate}
            setStartDate={setStartDate}
            setFilterData={setFilterData}
            filterData={filterData}
          />
        </div>
        <div
          className="tab-pane fade"
          id="alert"
          role="tabpanel"
          aria-labelledby="alertTab"
        >
          <AlertScreen />
        </div>
      </div>
    </>
  );
};

export default AppActivity;
